/**
 * CaptainVladAI — pure behaviour tree for "Captain Vlad" Stage 7 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Ex-naval smuggling captain commanding the Dover RIB fleet.
 *
 * Phase 1 (HP > 40%): flare_shot → satchel_charge → rib_ram (round-robin)
 * Phase 2 (HP ≤ 40%): cycle-0 slot triggers a flare + satchel combo (both fire
 *   back-to-back before the cooldown resets and the cycle advances).
 *
 * Attack notes:
 *   flare_shot     — ranged bright bolt, fires at any range, boss backsteps
 *   satchel_charge — timed-bomb lob at any range, boss stands and watches
 *   rib_ram        — telegraphed RIB-dash across the shore lane (distance-gated)
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** RIB-ram dash engages within this pixel distance. */
export const RAM_RANGE = 220;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Telegraph frames before the flare bolt launches. */
export const FLARE_WINDUP_FRAMES = 24;
/** Telegraph frames before the satchel charge is lobbed. */
export const SATCHEL_WINDUP_FRAMES = 28;
/** Telegraph frames before the RIB-ram dash begins. */
export const RIB_RAM_WINDUP_FRAMES = 32;
/** Frames of active RIB-ram dash (hitbox live the whole time). */
export const RAM_FRAMES = 20;
/** Retreat frames after flare_shot. */
export const BACKSTEP_FRAMES = 26;
/** Forward speed multiplier during the RIB-ram dash. */
export const RAM_SPEED = 4.0;
/** Base inter-attack cooldown at aggressionScale = 0. */
const BASE_COOLDOWN = 80;

// ── HP phase threshold ────────────────────────────────────────────────────────
export const PHASE_2_THRESHOLD = 0.4;

// ── Types ─────────────────────────────────────────────────────────────────────
export type CaptainVladPhase = 1 | 2;

export type CaptainVladAttack = 'flare_shot' | 'satchel_charge' | 'rib_ram';

export type CaptainVladNode =
  | 'scan'
  | 'approach'
  | 'flare_windup'
  | 'satchel_windup'
  | 'rib_ram_windup'
  | 'ramming'
  | 'backstep';

export interface CaptainVladAIState {
  node: CaptainVladNode;
  phase: CaptainVladPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  ramFrames: number;
  /**
   * Direction the rib_ram dash committed to, latched when entering the `ramming`
   * node. Used for both dash movement and the terminal hit's facing so the dash
   * never reverses mid-charge — recomputing facing from live position jitters
   * 1/-1 at point-blank (selfX ≈ targetX) and can fire the hitbox away from the
   * player. 1 = dashing/striking right, -1 = left.
   */
  ramDir: 1 | -1;
  /** Round-robin: 0 = flare_shot, 1 = satchel_charge, 2 = rib_ram. */
  attackCycle: number;
  /**
   * Phase-2 combo flag: set on the frame flare_shot fires in phase 2.
   * Causes the next scan (after backstep) to immediately queue satchel_charge.
   * Cleared when satchel fires.
   */
  pendingComboSatchel: boolean;
}

export interface CaptainVladAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). Drives phase-transition checks each frame. */
  hpFraction: number;
}

export interface CaptainVladAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  /** The attack type fired this frame, or null. */
  attackType: CaptainVladAttack | null;
  nextState: CaptainVladAIState;
}

export function initialCaptainVladState(): CaptainVladAIState {
  return {
    node: 'scan',
    phase: 1,
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    ramFrames: 0,
    ramDir: 1,
    attackCycle: 0,
    pendingComboSatchel: false,
  };
}

/**
 * Advance the Captain Vlad behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns hitboxes.
 */
export function tickCaptainVladAI(
  input: CaptainVladAIInput,
  state: CaptainVladAIState,
): CaptainVladAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialCaptainVladState());
  }

  // ── Phase resolution (re-derived every frame from live HP) ────────────────
  const phase: CaptainVladPhase = input.hpFraction <= PHASE_2_THRESHOLD ? 2 : 1;

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const ramFrames = Math.max(0, state.ramFrames - 1);
  const { pendingComboSatchel, attackCycle } = state;

  // ── Hitstun: freeze animation, reset active states, keep ticking cooldown ─
  if (input.isHitstunned) {
    return noOp(facing, {
      ...state,
      phase,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
      ramFrames: 0,
      pendingComboSatchel,
    });
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * 1.3,
        moveY: away.y * 1.3,
        facing,
        wantsAttack: false,
        attackType: null,
        nextState: { ...state, phase, cooldown, backstepFrames, pendingComboSatchel },
      };
    }
    return noOp(facing, {
      ...state,
      phase,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
      pendingComboSatchel,
    });
  }

  // ── RIB_RAM_WINDUP ────────────────────────────────────────────────────────
  if (state.node === 'rib_ram_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, cooldown, windup, pendingComboSatchel });
    }
    return noOp(facing, {
      ...state,
      phase,
      cooldown,
      node: 'ramming',
      windup: 0,
      ramFrames: RAM_FRAMES,
      // Commit the dash direction now (boss is still at range, facing the
      // player). The ramming branch uses this — not the per-frame facing —
      // so the dash can't reverse and the terminal hit lands on the player.
      ramDir: input.targetX >= input.selfX ? 1 : -1,
      pendingComboSatchel,
    });
  }

  // ── RAMMING (active dash — hitbox live the full duration) ─────────────────
  if (state.node === 'ramming') {
    // Use the committed ramDir (latched at dash start), NOT the per-frame facing:
    // at point-blank selfX ≈ targetX makes facing jitter 1/-1 each frame, which
    // would reverse the dash and could fire the terminal hitbox away from the player.
    if (ramFrames > 0) {
      return {
        moveX: state.ramDir * RAM_SPEED,
        moveY: 0,
        facing: state.ramDir,
        wantsAttack: false,
        attackType: null,
        nextState: { ...state, phase, cooldown, ramFrames, pendingComboSatchel },
      };
    }
    // Dash complete — fire the ram hit on the committed side
    return {
      moveX: 0,
      moveY: 0,
      facing: state.ramDir,
      wantsAttack: true,
      attackType: 'rib_ram',
      nextState: {
        ...state,
        phase,
        node: 'scan',
        cooldown: cooldownFor(input.aggressionScale),
        ramFrames: 0,
        attackCycle: nextCycle(attackCycle),
        pendingComboSatchel,
      },
    };
  }

  // ── FLARE WINDUP ──────────────────────────────────────────────────────────
  if (state.node === 'flare_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, cooldown, windup, pendingComboSatchel });
    }
    // In phase 2, immediately queue a satchel combo: zero cooldown, hold attackCycle
    const comboQueued = phase === 2;
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'flare_shot',
      nextState: {
        ...state,
        phase,
        node: 'backstep',
        cooldown: comboQueued ? 0 : cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: comboQueued ? attackCycle : nextCycle(attackCycle),
        pendingComboSatchel: comboQueued,
      },
    };
  }

  // ── SATCHEL WINDUP ────────────────────────────────────────────────────────
  if (state.node === 'satchel_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, phase, cooldown, windup, pendingComboSatchel });
    }
    // Advance past both flare (0) and satchel (1) when fired as a combo
    const nextAtk = pendingComboSatchel ? (attackCycle + 2) % 3 : nextCycle(attackCycle);
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'satchel_charge',
      nextState: {
        ...state,
        phase,
        node: 'scan', // no backstep; boss watches the detonation
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        attackCycle: nextAtk,
        pendingComboSatchel: false,
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);

  if (cooldown === 0 && input.canAttack) {
    // Phase-2 combo: pending satchel skips the normal cycle check
    if (pendingComboSatchel) {
      return noOp(facing, {
        ...state,
        phase,
        cooldown,
        node: 'satchel_windup',
        windup: SATCHEL_WINDUP_FRAMES,
        pendingComboSatchel: true,
      });
    }

    if (attackCycle === 0) {
      // Flare bolt fires at any range
      return noOp(facing, {
        ...state,
        phase,
        cooldown,
        node: 'flare_windup',
        windup: FLARE_WINDUP_FRAMES,
        pendingComboSatchel: false,
      });
    } else if (attackCycle === 1) {
      // Satchel throw fires at any range
      return noOp(facing, {
        ...state,
        phase,
        cooldown,
        node: 'satchel_windup',
        windup: SATCHEL_WINDUP_FRAMES,
        pendingComboSatchel: false,
      });
    } else if (attackCycle === 2) {
      // RIB-ram is distance-gated
      if (dist <= RAM_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          cooldown,
          node: 'rib_ram_windup',
          windup: RIB_RAM_WINDUP_FRAMES,
          pendingComboSatchel: false,
        });
      }
    }
  }

  // ── Approach orbit position ───────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: CaptainVladNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      nextState: {
        ...state,
        phase,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
        ramFrames: 0,
        pendingComboSatchel,
      },
    };
  }

  return noOp(facing, { ...state, phase, cooldown, node: 'scan', pendingComboSatchel });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number): number {
  return Math.round(BASE_COOLDOWN * (1 - aggressionScale * 0.5));
}

function toTarget(input: CaptainVladAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: CaptainVladAIState): CaptainVladAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    nextState,
  };
}

function nextCycle(c: number): number {
  return (c + 1) % 3;
}
